import {Injectable} from '@angular/core';
import {ModalController, ToastController, ToastOptions} from "@ionic/angular/standalone";

import {ModalComponent} from "../shared/modals/generic-modal/modal.component";
import {ModalConfiguration, ModalList} from "../shared/interfaces/modal-configuration-interface";
import {ApplicantModalEnum, ModalRoleEnum} from "../shared/enums/modal-enum";
import {RecoverPasswordModalComponent} from "../shared/modals/recover-password/recover-password-modal.component";
import {ChooseChildModalComponent} from "../shared/modals/choose-child-modal/choose-child-modal.component";
import {ErrorRequestModalComponent} from "../shared/modals/error-request-modal/error-request-modal.component";
import {
  ChooseCalendarEventModalComponent
} from "../shared/modals/choose-calendar-event-modal/choose-calendar-event-modal.component";
import {
  DettaglioRicaricaModalComponent
} from "../shared/modals/dettaglio-ricarica-modal/dettaglio-ricarica-modal.component";
import {
  CommandPaletteComponent
} from "../shared/modals/command-palette/command-palette.component";
import {PathEnum} from "../shared/enums/path-enum";


@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  handlerMessage = '';
  roleMessage = '';
  private _defaultSnackbarConfig: ToastOptions = {
    message: '',
    color: 'success',
    position: 'bottom',
    duration: 5000,
    icon: 'information-circle-outline'
  };

  modals: ModalList = {};

  constructor(private toastController: ToastController,
              private modalController: ModalController) {
  }

  /**
   * Presenta una modal e ripristina il focus sull'elemento trigger dopo la chiusura.
   * Ionic ModalController non gestisce il focus return per le modal programmatiche:
   * il focus finisce su <body> dopo dismiss. Questo metodo salva document.activeElement
   * prima di present() e lo ripristina dopo onDidDismiss().
   */
  async presentModalWithFocusReturn(modal: HTMLIonModalElement): Promise<void> {
    const triggerElement = document.activeElement as HTMLElement | null;
    await modal.present();
    await modal.onDidDismiss();
    if (triggerElement && (document.activeElement === null || document.activeElement === document.body)) {
      triggerElement.focus();
    }
  }

  async closeAll() {
    const topModal = await this.modalController.getTop();
    if(!!topModal) {
      await this.dismissAll(topModal);
    }
  }

  dismissAll(modal: HTMLIonModalElement) {
    return new Promise ((resolve) => {
      modal.dismiss().then(async res => {
        const getNewTop = await this.modalController.getTop();
        if (getNewTop) {
          resolve(this.dismissAll(getNewTop));
        } else {
          resolve(true);
        }
      });
    });
  }

  openToast(_toastConfiguration: ToastOptions) {
    const config = { ...this._defaultSnackbarConfig, ..._toastConfiguration };
    return this.presentToast(config);
  }

  async presentToast(configuration: ToastOptions) {
    const toast = await this.toastController.create(configuration);
    await toast.present();
    const {role} = await toast.onDidDismiss()
    this.roleMessage = `Dismissed with role: ${role}`;
  }

  async openModal(configuration: ModalConfiguration, applicant?: string): Promise<{ [key: string]: any } | null>{
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: ModalComponent,
        componentProps: configuration,
        cssClass: configuration.cssClass,
        backdropDismiss: !!configuration.backdropDismiss,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
          this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onDidDismiss().then(
        (value) => {
          if (!!applicant) {
            this.setModalByApplicant(applicant, undefined)
          }
          this.restoreFocus(triggerElement);
          return value;
        }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      return null;
    }
    return null
  }

  async openRecoverPasswordModal(applicant?: string): Promise<{ [key: string]: any } | null>{
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: RecoverPasswordModalComponent,
        cssClass: 'generic-modal',
        backdropDismiss: false,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
          this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onWillDismiss().then(
        (value) => {
          if (!!applicant) {
            this.setModalByApplicant(applicant, undefined)
          }
          this.restoreFocus(triggerElement);
          return value;
        }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      return null;
    }
    return null
  }

  async openChooseChildModal(applicant?: string): Promise<{ [key: string]: any } | null>{
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: ChooseChildModalComponent,
        cssClass: 'generic-modal',
        backdropDismiss: false,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
          this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onWillDismiss().then(
        (value) => {
          if (!!applicant) {
            this.setModalByApplicant(applicant, undefined)
          }
          this.restoreFocus(triggerElement);
          return value;
        }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      return null;
    }
    return null
  }

  async openErrorModal(configuration: ModalConfiguration, applicant?: string): Promise<{ [key: string]: any } | null> {
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: ErrorRequestModalComponent,
        componentProps: configuration,
        cssClass: configuration.cssClass,
        backdropDismiss: !!configuration.backdropDismiss,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
            this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onDidDismiss().then(
          (value) => {
            if (!!applicant) {
              this.setModalByApplicant(applicant, undefined)
            }
            this.restoreFocus(triggerElement);
            return value;
          }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      if (role === ModalRoleEnum.CANCEL) {
        return data
      }
      return null;
    }
    return null
  }


  async openCalendarChoiceModal(configuration: ModalConfiguration, applicant?: string): Promise<{ [key: string]: any } | null> {
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: ChooseCalendarEventModalComponent,
        componentProps: configuration,
        cssClass: configuration.cssClass,
        backdropDismiss: !!configuration.backdropDismiss,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
            this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onWillDismiss().then(
          (value) => {
            if (!!applicant) {
              this.setModalByApplicant(applicant, undefined)
            }
            this.restoreFocus(triggerElement);
            return value;
          }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      return null;
    }
    return null
  }

  async openRicaricaModal(configuration: ModalConfiguration, applicant?: string): Promise<{ [key: string]: any } | null> {
    const isModalYetOpened = !!applicant && this.getModalByApplicant(applicant);
    if (!isModalYetOpened) {
      const triggerElement = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        component: DettaglioRicaricaModalComponent,
        componentProps: configuration,
        cssClass: configuration.cssClass,
        backdropDismiss: !!configuration.backdropDismiss,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });
      if (!!applicant) {
        modal.present().then(() =>
          this.setModalByApplicant(applicant, modal)
        );
      } else {
        modal.present();
      }

      const {data, role} = await modal.onWillDismiss().then(
        (value) => {
          if (!!applicant) {
            this.setModalByApplicant(applicant, undefined)
          }
          this.restoreFocus(triggerElement);
          return value;
        }
      );
      if (role === ModalRoleEnum.CONFIRM) {
        return data
      }
      return null;
    }
    return null
  }


  //----- MARK: SET e GET delle modali -----

  setPushModal(modal: HTMLIonModalElement | undefined) {
    this.modals.pushModal = modal;
  }

  getPushModal(): HTMLIonModalElement | undefined {
    return this.modals.pushModal;
  }

  getModalByApplicant(applicant?: ApplicantModalEnum | string): HTMLIonModalElement | undefined {
    switch (applicant) {
      default:
        return (!!applicant && !!this.modals[applicant]) ? this.modals[applicant] : undefined;
    }
  }

  setModalByApplicant(applicant: ApplicantModalEnum | string, modal: HTMLIonModalElement | undefined) {
    switch (applicant) {
      default:
        this.modals[applicant] = modal;
        break;
    }
  }

//----- ENDMARK: SET e GET delle modali -----

//----- MARK: DISMISS delle modali -----
  dismissAllModals() {
    Object.values(this.modals).forEach((modal) => {
      if (!!modal) {
        modal.dismiss();
      }
    });
  }

//----- ENDMARK: DISMISS delle modali -----

//----- MARK: FOCUS RETURN (WCAG 2.4.3) -----
  private restoreFocus(triggerElement: HTMLElement | null): void {
    if (triggerElement && (document.activeElement === null || document.activeElement === document.body)) {
      triggerElement.focus();
    }
  }
//----- ENDMARK: FOCUS RETURN -----

//----- MARK: COMMAND PALETTE -----
  async openCommandPalette(): Promise<any | null> {
    const triggerElement = document.activeElement as HTMLElement | null;
    // backdropDismiss:true → un tap sul backdrop chiude la palette (dismiss con role di default,
    // trattato come annullamento dal chiamante). L'etichetta accessibile è sul `<div role="dialog">`
    // interno (tradotto via Transloco): non la si duplica qui sull'host `ion-modal` per non annidare
    // due dialog con label ridondante.
    const modal = await this.modalController.create({
      component: CommandPaletteComponent,
      cssClass: 'command-palette-modal',
      backdropDismiss: true,
    });
    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    this.restoreFocus(triggerElement);

    if (role === ModalRoleEnum.CONFIRM) {
      return data;
    }
    return null;
  }
//----- ENDMARK: COMMAND PALETTE -----

}
